import { ReactNode, useEffect, useRef, useState } from 'react';
import { Box, Paper } from '@mui/material';
import { DragIndicator } from '@mui/icons-material';
import {
  dragPanel,
  PanelResizeCorner,
  resizePanel,
} from '../services/draggablePanel.service';

interface IProps {
  children: ReactNode;
  /** Startbreite des schwebenden Panels (px). */
  width?: number;
  /** Starthöhe des schwebenden Panels (px). */
  height?: number;
}

type Mode = 'idle' | 'drag' | PanelResizeCorner;

/**
 * Schwebendes, verschieb- UND größenveränderbares Overlay-Panel (#1181).
 *
 * Wird für die „Bild-in-Bild"-Ansicht von Dokumenten/Rechnungen verwendet:
 * Statt das Formular in eine schmale Spalte zu quetschen, legt sich die
 * Vorschau als bewegliches Fenster über den Dialog. Verschoben wird an der
 * Griffleiste oben, in der Größe gezogen an den Griffen unten rechts **und
 * oben rechts** (QS #8: an nur einer Ecke ziehen zu können war nicht
 * verständlich). Am oberen Griff bleibt die Unterkante stehen. Position ist
 * `fixed`, damit das Panel beim Scrollen des Formulars nicht wegläuft.
 */
export const DraggableSidepanel = ({
  children,
  width = 600,
  height = 700,
}: IProps) => {
  const [pos, setPos] = useState({ top: 90, left: 24 });
  const [size, setSize] = useState({ width, height });
  const [mode, setMode] = useState<Mode>('idle');
  const start = useRef({
    mouseX: 0,
    mouseY: 0,
    top: 0,
    left: 0,
    width: 0,
    height: 0,
  });

  const begin =
    (nextMode: Exclude<Mode, 'idle'>) => (event: React.MouseEvent) => {
      start.current = {
        mouseX: event.clientX,
        mouseY: event.clientY,
        top: pos.top,
        left: pos.left,
        width: size.width,
        height: size.height,
      };
      setMode(nextMode);
      event.preventDefault();
      event.stopPropagation();
    };

  useEffect(() => {
    if (mode === 'idle') {
      return;
    }
    const onMove = (event: MouseEvent) => {
      const dx = event.clientX - start.current.mouseX;
      const dy = event.clientY - start.current.mouseY;
      const viewport = {
        width: window.innerWidth,
        height: window.innerHeight,
      };

      if (mode === 'drag') {
        setPos(dragPanel(start.current, dx, dy, viewport));
        return;
      }

      const box = resizePanel(start.current, dx, dy, mode, viewport);
      setSize({ width: box.width, height: box.height });
      setPos({ top: box.top, left: box.left });
    };
    const onUp = () => setMode('idle');
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [mode]);

  /** Gemeinsames Aussehen der beiden Größengriffe. */
  const resizeHandleSx = {
    position: 'absolute' as const,
    right: 3,
    width: 12,
    height: 12,
    cursor: 'nesw-resize',
    borderColor: 'text.secondary',
    opacity: 0.5,
  };

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        width: size.width,
        height: size.height,
        maxWidth: '98vw',
        maxHeight: '95vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: 2,
        zIndex: theme => theme.zIndex.modal + 1,
      }}
    >
      {/* Griff zum Vergrößern/Verkleinern (oben rechts). Liegt über der
          Griffleiste, damit das Ziehen nicht als Verschieben ankommt. */}
      <Box
        onMouseDown={begin('ne')}
        title='Größe ändern'
        sx={{
          ...resizeHandleSx,
          top: 3,
          borderRight: '2px solid',
          borderTop: '2px solid',
          zIndex: 1,
        }}
      />
      <Box
        onMouseDown={begin('drag')}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 0.25,
          flexShrink: 0,
          cursor: mode === 'drag' ? 'grabbing' : 'grab',
          bgcolor: 'action.hover',
          borderBottom: 1,
          borderColor: 'divider',
          userSelect: 'none',
        }}
      >
        <DragIndicator fontSize='small' color='disabled' />
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>{children}</Box>
      {/* Griff zum Vergrößern/Verkleinern (unten rechts). */}
      <Box
        onMouseDown={begin('se')}
        title='Größe ändern'
        sx={{
          ...resizeHandleSx,
          bottom: 3,
          cursor: 'nwse-resize',
          borderRight: '2px solid',
          borderBottom: '2px solid',
        }}
      />
    </Paper>
  );
};
